// views/Dashboard/Bonuses/components/RiskGauge/RiskGauge.tsx
import { useMemo } from 'react';
import { Risk } from '../../../../../Entities';
import styles from './RiskGauge.module.css';

interface RiskGaugeProps {
  /** The active risk (matched from bonus.risk_rate). */
  risk: any;
  /** Optional explicit list of risk levels; defaults to the backend Risk constants. */
  levels?: any[];
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const SIZES = {
  small: { w: 64, stroke: 4.5, hub: 3, font: 0.58 },
  medium: { w: 100, stroke: 6.5, hub: 4.5, font: 0.7 },
  large: { w: 150, stroke: 9, hub: 6.5, font: 0.85 },
};

// Semicircle sweeps from 180° (left) to 0° (right).
const START_ANGLE = 180;
const SPAN = 180;

const polar = (cx: number, cy: number, r: number, angleDeg: number) => {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
};

const arcPath = (cx: number, cy: number, r: number, startDeg: number, endDeg: number) => {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

const RiskGauge = ({ risk, levels, size = 'small', showLabel = true }: RiskGaugeProps) => {
  const sorted = useMemo(() => {
    const source = (levels && levels.length ? levels : Risk.values) || [];
    return [...source].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }, [levels]);

  const dims = SIZES[size];
  const cx = dims.w / 2;
  const cy = cx;
  const r = cx - dims.stroke;
  const height = cy + dims.hub + 2;

  const count = sorted.length || 1;
  const segSpan = SPAN / count;
  const gap = Math.min(2.5, segSpan * 0.1);

  const activeIdx = useMemo(() => {
    if (!risk) return -1;
    const byId = sorted.findIndex((l) => l.id === risk.id);
    if (byId !== -1) return byId;
    return sorted.findIndex((l) => l.rate === risk.rate);
  }, [risk, sorted]);

  const active = activeIdx !== -1 ? sorted[activeIdx] : risk;
  const needleAngle = activeIdx === -1 ? 90 : START_ANGLE - (activeIdx + 0.5) * segSpan;
  const needleLen = r - dims.stroke / 2 - 1.5;

  return (
    <div className={styles.gauge} style={{ width: dims.w }}>
      <svg
        viewBox={`0 0 ${dims.w} ${height}`}
        width={dims.w}
        height={height}
        role="img"
        aria-label={active ? `Riziko: ${active.label}` : 'Riziko'}
      >
        {sorted.map((level, i) => {
          const segStart = START_ANGLE - i * segSpan;
          const segEnd = START_ANGLE - (i + 1) * segSpan;
          const isActive = i === activeIdx;
          return (
            <path
              key={level.id ?? i}
              d={arcPath(cx, cy, r, segStart - gap, segEnd + gap)}
              stroke={isActive ? level.color : level.color_not_clicked || level.color}
              strokeWidth={isActive ? dims.stroke + 1.5 : dims.stroke}
              fill="none"
              opacity={isActive ? 1 : 0.4}
              className={styles.segment}
            />
          );
        })}

        {/* Needle: drawn pointing left (180°), rotated into place so it animates */}
        <g
          className={styles.needle}
          style={{
            transform: `rotate(${180 - needleAngle}deg)`,
            transformOrigin: `${cx}px ${cy}px`,
          }}
        >
          <polygon
            points={`${cx - needleLen},${cy} ${cx},${cy - dims.hub * 0.55} ${cx + dims.hub * 1.3},${cy} ${cx},${cy + dims.hub * 0.55}`}
            fill={active?.color || 'var(--ink)'}
          />
        </g>
        <circle cx={cx} cy={cy} r={dims.hub} fill="var(--ink)" opacity={0.85} />
        <circle cx={cx} cy={cy} r={dims.hub * 0.45} fill="var(--card)" />
      </svg>

      {showLabel && active && (
        <div className={styles.label} style={{ color: active.color, fontSize: `${dims.font}rem` }}>
          {active.label}
        </div>
      )}
    </div>
  );
};

export default RiskGauge;
